import React from 'react';
import { Box } from '@mui/material';
import DOMPurify from 'dompurify';

/**
 * HtmlRenderer Component
 * Renders HTML content with proper sanitization to prevent XSS attacks
 *
 * @param {Object} props Component props
 * @param {string} props.content HTML content to be rendered
 * @param {string} props.className Additional class name for styling
 * @returns {React.ReactElement} Rendered HTML content
 */
const HtmlRenderer = ({ content, className }) => {
  // Configure DOMPurify to allow certain tags and attributes
  const sanitize = (html) => {
    if (!html) return '';

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr',
        'ul', 'ol', 'li', 'dl', 'dt', 'dd',
        'b', 'strong', 'i', 'em', 'mark', 'small', 'del', 'ins', 'sub', 'sup',
        'blockquote', 'pre', 'code',
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
        'a', 'img', 'figure', 'figcaption',
        'div', 'span'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'title', 'rel', 'src', 'alt', 'class', 'id',
        'width', 'height', 'style'
      ],
      ALLOW_DATA_ATTR: false
    });
  };

  const sanitizedContent = sanitize(content);

  return (
    <Box 
      className={className}
      sx={{ 
        '& img': { maxWidth: '100%', height: 'auto' },
        '& table': { 
          width: '100%', 
          borderCollapse: 'collapse',
          marginBottom: '1rem',
          border: '1px solid #dee2e6'
        },
        '& th, & td': { 
          padding: '0.75rem',
          border: '1px solid #dee2e6'
        },
        '& blockquote': {
          borderLeft: '4px solid #e5e5e5',
          margin: '1rem 0',
          padding: '0.5rem 1rem'
        },
        '& pre': {
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '4px',
          overflow: 'auto'
        },
        '& code': {
          backgroundColor: '#f8f9fa',
          padding: '0.2rem 0.4rem',
          borderRadius: '4px',
          fontSize: '87.5%'
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          marginTop: '1.5rem',
          marginBottom: '1rem',
          fontWeight: 'bold',
          lineHeight: '1.2'
        },
        '& p': {
          marginTop: '0',
          marginBottom: '1rem'
        },
        '& ul, & ol': {
          marginTop: '0',
          marginBottom: '1rem',
          paddingLeft: '2rem'
        }
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

export default HtmlRenderer;